import { useMemo } from "react";

import type {
  AppPermission,
  AppRole,
  AuthorizationRequirement,
} from "../types/permissions";
import {
  checkAuthorization,
  hasPermission,
  resolvePermissionsForRoles,
} from "../utils/authorization";
import { useAuth } from "./useAuth";

export interface UseAuthorizationReturn {
  /** Checks if the user possesses a specific permission. */
  can: (permission: AppPermission) => boolean;
  /** Checks if the user possesses all specified permissions (AND). */
  canAll: (permissions: readonly AppPermission[]) => boolean;
  /** Checks if the user possesses at least one of the specified permissions (OR). */
  canAny: (permissions: readonly AppPermission[]) => boolean;
  /** Checks if the user has a specific role in the active school tenant. */
  hasRole: (role: AppRole) => boolean;
  /** Checks if the user has any of the specified roles in the active tenant. */
  hasAnyRole: (roles: readonly AppRole[]) => boolean;
  /** Comprehensive check verifying arbitrary permission/role criteria. */
  check: (requirement: AuthorizationRequirement) => boolean;
  /** All unique permission strings granted by current active roles. */
  grantedPermissions: readonly AppPermission[];
  /** Current active tenant roles. */
  roles: readonly string[];
}

/**
 * Hook providing permission- and role-based authorization capabilities.
 * Decouples authorization logic from UI components.
 */
export function useAuthorization(): UseAuthorizationReturn {
  const { roles } = useAuth();

  const grantedPermissions = useMemo(() => {
    return resolvePermissionsForRoles(roles);
  }, [roles]);

  return {
    can: (permission: AppPermission) =>
      hasPermission(grantedPermissions, permission),

    canAll: (permissions: readonly AppPermission[]) =>
      permissions.every((p) => hasPermission(grantedPermissions, p)),

    canAny: (permissions: readonly AppPermission[]) =>
      permissions.some((p) => hasPermission(grantedPermissions, p)),

    hasRole: (role: AppRole) => roles.includes(role),

    hasAnyRole: (candidateRoles: readonly AppRole[]) =>
      candidateRoles.some((role) => roles.includes(role)),

    check: (requirement: AuthorizationRequirement) =>
      checkAuthorization(roles, requirement),

    grantedPermissions,
    roles,
  };
}
